import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PurchaseOrder } from '../_interfaces/purchase-order';

// A row from the paginated list (POST /purchase-orders/list) - lighter
// than PurchaseOrder: items aren't loaded individually, just aggregated
// (itemCount, totalCost) since the list only ever displays the totals -
// see PurchaseOrdersService.getPurchaseOrders in nustone-backend.
export interface PurchaseOrderListItem extends Omit<PurchaseOrder, 'items'> {
  itemCount: number;
  // Units received so far, against itemCount (units ordered).
  receivedCount: number;
  totalCost: number;
}

export interface PurchaseOrderQuery {
  pageIndex: number;
  pageSize: number;
  search?: string;
  status?: string;
  billingStatus?: string;
  lateOnly?: boolean;
}

export interface PurchaseOrderSummary {
  open: number;
  late: number;
  awaitingPayment: number;
  overdue: number;
}

export interface PurchaseOrderQueryResult {
  items: PurchaseOrderListItem[];
  count: number;
  // Orders per status with every filter except status applied (for the tabs).
  statusCounts: Record<string, number>;
  summary: PurchaseOrderSummary;
}

export interface PurchaseOrderItemInput {
  productId: number | null;
  productName: string;
  sku?: string;
  quantity: number;
  quantityReceived?: number;
  quantityBilled?: number;
  unitCost: number;
  unitWeight?: number;
  taxRate?: number;
}

export interface PurchaseOrderInput {
  supplierId: number | null;
  buyerName: string;
  store: string;
  currency: string;
  orderDate: string;
  expectedDeliveryDate?: string | null;
  status: string;
  billingStatus: string;
  sourceDocument?: string;
  incoterms?: string;
  paymentTerms?: string;
  portOfLoading?: string;
  portOfDischarge?: string;
  notes?: string;
  allocationMethod?: 'VALUE' | 'QUANTITY' | 'WEIGHT';
  exchangeRate?: number;
  charges?: { chargeType: string; description?: string | null; amount: number }[];
  items: PurchaseOrderItemInput[];
}

// Backed by /purchase-orders/* - see purchase-orders.controller.ts /
// purchase-orders.service.ts in nustone-backend.
@Injectable({
  providedIn: 'root',
})
export class PurchaseOrderService {
  constructor(private http: HttpClient) {}

  getPurchaseOrders(query: PurchaseOrderQuery): Observable<PurchaseOrderQueryResult> {
    return this.http.post<PurchaseOrderQueryResult>('/purchase-orders/list', query);
  }

  getPurchaseOrderById(id: number): Observable<PurchaseOrder> {
    return this.http.get<PurchaseOrder>(`/purchase-orders/${id}`);
  }

  createPurchaseOrder(input: PurchaseOrderInput): Observable<PurchaseOrder> {
    return this.http.post<PurchaseOrder>('/purchase-orders', input);
  }

  updatePurchaseOrder(id: number, input: PurchaseOrderInput): Observable<PurchaseOrder> {
    return this.http.put<PurchaseOrder>(`/purchase-orders/${id}`, input);
  }
}
